import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Menu, CreditCard } from "lucide-react";
import { BASE_URL } from "../../constant/constant";
import { useNavigate } from "react-router-dom";

const TopBar = ({ onToggleSidebar, sidebarOpen, isMobile = false, currentPageTitle = null, currentPageBadge = null }) => {
  const { user } = useSelector((state) => state.auth);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const getUserName = () => {
    if (user?.firstName || user?.lastName) {
      return `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    }
    return user?.email || "User";
  };

  const userName = getUserName();

  const getProfileImageUrl = () => {
    const profilePicture = user?.profilePicture;

    if (profilePicture) {
      if (typeof profilePicture === "string") {
        return profilePicture.startsWith("http")
          ? profilePicture
          : `${BASE_URL}${profilePicture}`;
      }

      if (typeof profilePicture === "object") {
        const pictureUrl = profilePicture?.url || profilePicture?.src;
        if (pictureUrl) {
          return pictureUrl.startsWith("http")
            ? pictureUrl
            : `${BASE_URL}${pictureUrl}`;
        }
      }
    }

    const initials = `${user?.firstName?.[0] || "N"}${user?.lastName?.[0] || "K"}`;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&background=1E6C56&color=FFFFFF&size=128`;
  };

  const profileImageSrc = getProfileImageUrl();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className=" shadow-sm border-b border-gray-200 px-4 md:px-6 py-4 m-2 md:m-4 rounded-lg"
      style={{
        background: "linear-gradient(to right, #1E6C56, #FFB800)",
      }}
    >
      <div className="flex items-center justify-between">
        {/* Left Side - Hamburger Menu and Welcome Message/Page Title */}
        <div className="flex items-center gap-2 md:gap-4">
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100/30 transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <h1 className="text-lg md:text-xl font-semibold text-white truncate">
              {currentPageTitle
                ? currentPageTitle
                : (isMobile ? `Hi, ${userName.split(" ")[0]}!` : `Welcome, ${userName}!`)
              }
            </h1>
            {currentPageTitle && currentPageBadge && (
              <span className="px-2.5 py-0.5 text-xs font-medium text-white bg-[#F28C00] rounded-full shadow-sm flex-shrink-0">
                {currentPageBadge}
              </span>
            )}
          </div>
        </div>

        {/* Right Side - Notifications and User */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* <div className="relative border-2 border-white/50 rounded-full p-1" >
            <Bell className="w-4 h-4 md:w-4 md:h-4 text-white cursor-pointer hover:text-gray-100 transition-colors" />
            <div className="absolute -top-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-red-500 rounded-full"></div>
          </div> */}


          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center gap-1 md:gap-2 hover:bg-white/10 rounded-lg p-1 transition-colors"
            >
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center overflow-hidden bg-white/20">
                <img
                  src={profileImageSrc}
                  alt={userName || "User"}
                  className="w-8 h-8 md:w-8 md:h-8 rounded-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=NK&background=1E6C56&color=FFFFFF&size=128`;
                  }}
                />
              </div>
              <p className="text-white text-sm md:text-base">{userName}</p>
            </button>

            {/* User Dropdown Menu */}
            {showUserDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={() => {
                    navigate("/user-dashboard/upgrade-plan");
                    setShowUserDropdown(false);
                  }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  Upgrade Plan
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;